import type { IntakeShotCapture } from "@/lib/api/public-consumer"
import type { VehicleSide } from "@/lib/api/types"
import type { DamageZone } from "../types"
import type { PhoneVerdict, SeenAs } from "./brain/combine"
import type { ViewClass } from "./brain/head"

// Consumer photo walk — shared vocabulary.
//
// The walk is DERIVED from the damage zones the consumer tapped
// (shot-plan.ts); only what they actually captured is stored. Shot ids are
// stable strings so a captured photo survives the plan being rebuilt (a zone
// added later never reshuffles what's already taken).

// Where the consumer stands for a whole-car shot. Driver = left (US).
export type Corner = "FL" | "FR" | "RR" | "RL"

export type ShotKind = "wide" | "closer" | "closeup" | "sticker"

export type ShotId =
  | `wide:${Corner}`
  | `closer:${Corner}`
  | `closeup:${DamageZone}`
  | "closeup:any"
  | "sticker"

export interface PlannedShot {
  id: ShotId
  kind: ShotKind
  // Stand position for wide / closer shots.
  corner: Corner | null
  // The damaged panel a close-up is for (null for "closeup:any").
  zone: DamageZone | null
  // What the photo is filed under on the estimate (and the evaluator hint).
  side: VehicleSide | null
  // Optional shots never block "Send photos".
  optional: boolean
  // Only whole-car shots may take themselves (smart camera).
  autoSnap: boolean
}

export type ShotStatus =
  | "uploading" // evaluating now (or waiting for a slot)
  | "queued" // offline — will upload when the connection is back
  | "failed" // gave up after retries — tap to retry
  | "evaluated" // checked by the AI, not yet on the lead
  | "attached" // on the lead (desktop mirror can see it)

export type ShotVerdict = "good" | "wrong-side" | "dark" | "blurry" | "too-close" | "too-far" | "low-quality"

// The phone's own look at a photo (capture/brain): which view of the car it
// shows, and what that means for the step it was taken for.
export interface PhoneCheck {
  verdict: PhoneVerdict["kind"]
  // Where the photo seems to have been taken, when it's the wrong corner.
  seen: SeenAs | null
  view: ViewClass | null
  // How sure the brain is of `view` (0–1).
  confidence: number
}

export type ShotSource = "auto" | "manual" | "native" | "gallery" | "desktop"

export interface ShotRecord {
  // A ShotId, or `extra:<id>` for photos that don't fill a planned slot.
  key: string
  status: ShotStatus
  // blob: URL this session, else the uploaded URL. Never persisted as blob:.
  previewUrl: string
  uploadedUrl: string
  plannedSide: VehicleSide | null
  detectedSide: VehicleSide | null
  qualityScore: number
  qualified: boolean
  qualityIssues: string[]
  description: string | null
  // null until the AI check comes back.
  verdict: ShotVerdict | null
  // The phone's check: undefined while it looks (a second or less), null
  // when it couldn't (no brain on this phone, out of time, restored photos).
  phone?: PhoneCheck | null
  // The consumer looked at a flagged photo and kept it ("Use this anyway" /
  // "Yes, that's right") — never nag about it again.
  accepted: boolean
  source: ShotSource
  retakes: number
  takenAt: number
  // How the live camera took it (timings), sent with the photo.
  capture?: IntakeShotCapture | null
}

export interface CaptureState {
  zones: DamageZone[]
  // Tapped "Not sure" instead of zones.
  unsure: boolean
  // Answered "Where's the damage?" (zones or unsure).
  zonesConfirmed: boolean
  shots: Partial<Record<ShotId, ShotRecord>>
  // Gallery / desktop photos that didn't fill a slot — still sent.
  extras: ShotRecord[]
  skipped: ShotId[]
  // Voice coach on/off (off by default). Lives in the existing draft key.
  voice: boolean
  // Set when the consumer tapped "Send photos" (final append fired).
  photosDoneAt: number | null
}

export const EMPTY_CAPTURE: CaptureState = {
  zones: [],
  unsure: false,
  zonesConfirmed: false,
  shots: {},
  extras: [],
  skipped: [],
  voice: false,
  photosDoneAt: null,
}

// Everything the server needs to place a photo — mirrors IntakeShotRef.
export interface ShotRef {
  id: string
  kind: ShotKind | "extra"
  zone: DamageZone | null
}
